<template>
  <div class="performanceInfo">
    <!-- 返利确认提示 -->
    <el-alert
      class="performanceInfoAlert"
      type="info"
      :closable="false"
      show-icon
      :title="rebateAlertText"
    />

    <!-- 顶部：等级 Tab + 返利确认 -->
    <div class="performanceInfoHeader">
      <div class="performanceInfoTabs">
        <button
          v-for="tab in levelTabList"
          :key="tab.value"
          type="button"
          class="performanceInfoTab"
          :class="{ 'is-active': activeLevel === tab.value }"
          @click="switchLevelTab(tab.value)"
        >
          {{ tab.label }}
        </button>
      </div>
      <el-button
        v-if="showRebateConfirmBtn"
        type="primary"
        class="performanceInfoConfirmBtn"
        @click="openRebateConfirmModal"
      >
        返利额确认
      </el-button>
    </div>

    <!-- 商务政策与账号/License 字段展示 -->
    <div class="performanceInfoPolicyWrap">
      <div class="performanceInfoPolicyRow">
        <span class="policyLabel">{{ activeLevel }}业务的商务政策：</span>
        <span class="policyValue">{{
          activeLevel === "L1" ? l1Policy : l2Policy
        }}</span>
      </div>

      <div v-if="activeLevel === 'L1'" class="performanceInfoPolicyRow mt-12">
        <span class="policyLabel">已申请License：</span>
        <span class="policyValue highlight">{{ l1AppliedLicense || "-" }}</span>
      </div>
      <div
        v-else-if="activeLevel === 'L2'"
        class="performanceInfoPolicyRow mt-12"
      >
        <span class="policyLabel">L-Design账号：</span>
        <span class="policyValue highlight">{{ l2LdesignAccount || "-" }}</span>
      </div>
    </div>

    <!-- 返利明细表格 -->
    <el-table
      class="performanceInfoTable"
      :data="currentLevelData.rebateList"
      border
      stripe
      v-loading="tableLoading"
      empty-text="暂无业绩数据"
    >
      <el-table-column
        label="授权期内有效业绩期"
        min-width="180"
        align="center"
      >
        <template slot-scope="scope">
          {{ formatPeriod(scope.row.startDate, scope.row.endDate) }}
        </template>
      </el-table-column>
      <el-table-column
        v-if="activeLevel === 'L2'"
        prop="seaCount"
        label="SEA数智盘柜台数"
        min-width="120"
        align="center"
      />
      <el-table-column
        label="业绩金额(智能化清单)"
        min-width="150"
        align="center"
      >
        <template slot-scope="scope">
          {{ formatAmount(scope.row.performanceAmount) }}
        </template>
      </el-table-column>
      <el-table-column label="返利系数" min-width="100" align="center">
        <template slot-scope="scope">
          {{ formatCoefficient(scope.row.rebateCoefficient) }}
        </template>
      </el-table-column>
      <el-table-column label="可获得返利额(元)" min-width="150" align="center">
        <template slot-scope="scope">
          <span class="performanceInfoAmount">
            {{ formatAmount(scope.row.availableRebate) }}
          </span>
        </template>
      </el-table-column>
    </el-table>

    <!-- 返利额确认弹窗 -->
    <div
      v-if="rebateConfirmVisible"
      class="rebateConfirmOverlay"
    >
      <div class="rebateConfirmCard" role="dialog" aria-modal="true">
        <div class="rebateConfirmHeader">
          <h3 class="rebateConfirmTitle">返利额确认</h3>
          <button
            type="button"
            class="rebateConfirmClose"
            aria-label="关闭"
            @click="closeRebateConfirmModal"
          >
            <i class="el-icon-close"></i>
          </button>
        </div>
        <div class="rebateConfirmBody">
          <div class="rebateConfirmRows">
            <div class="rebateConfirmRow">
              <span class="rebateConfirmRowLabel">授权期内有效业绩期</span>
              <span class="rebateConfirmRowValue">{{
                formatPeriod(
                  rebateConfirmDetail.startDate,
                  rebateConfirmDetail.endDate
                ) || "--"
              }}</span>
            </div>
            <div class="rebateConfirmRow">
              <span class="rebateConfirmRowLabel">业绩金额(智能化清单)</span>
              <span class="rebateConfirmRowValue">{{
                formatAmount(rebateConfirmDetail.performanceAmount) || "--"
              }}</span>
            </div>
            <div class="rebateConfirmRow">
              <span class="rebateConfirmRowLabel">返利系数</span>
              <span class="rebateConfirmRowValue">{{
                formatCoefficient(rebateConfirmDetail.rebateCoefficient) || "--"
              }}</span>
            </div>
            <div class="rebateConfirmRow">
              <span class="rebateConfirmRowLabel">可获得返利额(元)</span>
              <span class="rebateConfirmRowAmount">{{
                formatRebateMoney(rebateConfirmDetail.availableRebate)
              }}</span>
            </div>
          </div>
          <div class="rebateConfirmWarn">
            <i class="el-icon-warning rebateConfirmWarnIcon"></i>
            <p class="rebateConfirmWarnText">{{ rebateConfirmWarnText }}</p>
          </div>
        </div>
        <div class="rebateConfirmFooter">
          <el-button
            class="rebateConfirmCancelBtn"
            @click="closeRebateConfirmModal"
          >
            暂不确认
          </el-button>
          <el-button
            type="primary"
            class="rebateConfirmSubmitBtn"
            :loading="confirmLoading"
            @click="submitRebateConfirm"
          >
            确认无误
          </el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getPerformanceList, confirmRebate, getSysConfigList } from "@/api/lx";
/**
 * 业绩信息模块
 * 对应设计稿：L1/L2 业绩 Tab、返利确认、License 统计及商务政策、年度返利表格
 */
export default {
  name: "PerformanceInfo",
  props: {
    /** 父组件传入的企业信息，可用于关联盘厂等级等（接口就绪后使用） */
    company: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      /** 当前选中的业绩等级 Tab：L1 | L2 */
      activeLevel: "L1",
      /** 返利确认弹窗是否显示 */
      rebateConfirmVisible: false,
      /** 弹窗内展示的待确认返利明细 */
      rebateConfirmDetail: {
        id: "",
        year: "",
        rebateCoefficient: "",
        availableRebate: "",
        startDate: "",
        endDate: "",
        performanceAmount: "",
      },
      /** 弹窗内橙色提示文案 */
      rebateConfirmWarnText:
        "请仔细核对返利金额，确认后不可修改。如有疑问请联系业务专员核实。",
      /** 「确认无误」按钮 loading */
      confirmLoading: false,
      /** 表格 loading（拉取业绩数据时） */
      tableLoading: false,
      /** 顶部提示文案 */
      rebateAlertText:
        "请确认可获得返利额数据是否有误，数据无误请点击【返利额确认】按钮，数据有误请联系业务专员修改数据。",
      /** L1 商务政策下载链接 */
      l1Policy: "",
      /** L2 商务政策下载链接 */
      l2Policy: "",
      /**
       * 各等级业绩数据
       * rebateList: 按年份的返利明细
       */
      performanceDataMap: {
        L1: {
          rebateList: [],
        },
        L2: {
          rebateList: [],
        },
      },
    };
  },
  computed: {
    levelTabList() {
      const list =
        this.company && Array.isArray(this.company.equityClassifyList)
          ? this.company.equityClassifyList
          : [];
      const names = list.map((item) => item.equityClassifyName).filter(Boolean);
      const levelNames = names.filter((name) => ["L1", "L2"].includes(name));
      return levelNames.map((name) => {
        return {
          label: `${name}业绩信息`,
          value: name,
        };
      });
    },
    /** 当前 Tab 对应的业绩数据（统计 + 表格） */
    currentLevelData() {
      return (
        this.performanceDataMap[this.activeLevel] || {
          rebateList: [],
        }
      );
    },
    /** 当前等级对应的商务政策链接 */
    currentPolicyUrl() {
      return this.activeLevel === "L1" ? this.l1Policy : this.l2Policy;
    },
    /** 确认返利按钮是否显示，判断当前业绩数组第一条的 rebateConfirm === 0 */
    showRebateConfirmBtn() {
      const list = this.currentLevelData.rebateList || [];
      if (!list.length) return false;
      return list[0].rebateConfirm === 0;
    },
    l1AppliedLicense() {
      const list =
        this.company && Array.isArray(this.company.equityClassifyList)
          ? this.company.equityClassifyList
          : [];
      const l1 = list.find((item) => item.equityClassifyName === "L1");
      return l1 ? l1.appliedLicense : "-";
    },
    l2LdesignAccount() {
      const list =
        this.company && Array.isArray(this.company.equityClassifyList)
          ? this.company.equityClassifyList
          : [];
      const l2 = list.find((item) => item.equityClassifyName === "L2");
      return l2 ? l2.ldesignAccount : "-";
    },
  },
  watch: {
    company: {
      handler() {
        this.fetchPerformanceData();
        this.$nextTick(() => {
          this.initActiveLevel();
        });
      },
      deep: true,
    },
  },
  created() {
    this.fetchSysConfig();
    this.fetchPerformanceData();
    this.$nextTick(() => {
      this.initActiveLevel();
    });
  },
  methods: {
    initActiveLevel() {
      const tabs = this.levelTabList || [];
      if (tabs.length > 0) {
        const exists = tabs.some((tab) => tab.value === this.activeLevel);
        if (!exists) {
          this.activeLevel = tabs[0].value;
        }
      }
    },
    /**
     * 切换 L1 / L2 业绩 Tab
     * @param {string} level - 等级标识 L1 | L2
     */
    switchLevelTab(level) {
      if (this.activeLevel === level) return;
      this.activeLevel = level;
    },

    /**
     * 获取系统商务政策配置
     */
    fetchSysConfig() {
      getSysConfigList()
        .then((res) => {
          if (res.code === 200 && Array.isArray(res.data)) {
            const l1Config = res.data.find(
              (item) =>
                item.configType === "L1_BUSINESS_POLICY" ||
                item.configType === "L1" ||
                (item.configType &&
                  item.configType.toUpperCase().includes("L1"))
            );
            const l2Config = res.data.find(
              (item) =>
                item.configType === "L2_BUSINESS_POLICY" ||
                item.configType === "L2" ||
                (item.configType &&
                  item.configType.toUpperCase().includes("L2"))
            );
            this.l1Policy = (l1Config && l1Config.configContent) || "";
            this.l2Policy = (l2Config && l2Config.configContent) || "";
          }
        })
        .catch((err) => {
          console.error("获取系统商务政策配置失败", err);
        });
    },

    /**
     * 从后端加载真实的业绩列表数据
     */
    fetchPerformanceData() {
      this.tableLoading = true;
      getPerformanceList()
        .then((res) => {
          const groups = (res && res.data) || [];
          console.log("getPerformanceList groups:", groups);
          const l1Group = groups.find((item) => item.performanceLevel === "L1");
          const l2Group = groups.find((item) => item.performanceLevel === "L2");
          const l1List = l1Group ? l1Group.list || [] : [];
          const l2List = l2Group ? l2Group.list || [] : [];
          console.log("Parsed lists - L1:", l1List, "L2:", l2List);

          this.$set(this.performanceDataMap.L1, "rebateList", l1List);
          this.$set(this.performanceDataMap.L2, "rebateList", l2List);
        })
        .catch((err) => {
          console.error("加载业绩列表失败", err);
          this.$message.error("加载业绩数据失败");
        })
        .finally(() => {
          this.tableLoading = false;
        });
    },

    /**
     * 格式化日期
     */
    formatDateStr(str) {
      if (!str) return "";
      const datePart = str.split(" ")[0]; // 去除时间
      return datePart.replace(/-/g, ".");
    },

    /**
     * 格式化授权期内有效业绩期
     */
    formatPeriod(start, end) {
      if (!start && !end) return "";
      return `${this.formatDateStr(start)}-${this.formatDateStr(end)}`;
    },

    /**
     * 格式化返利系数
     */
    formatCoefficient(val) {
      if (val === undefined || val === null || val === "") return "";
      if (typeof val === "string" && val.includes("%")) return val;
      const num = Number(val);
      if (Number.isNaN(num)) return val;
      if (num > 0 && num < 1) {
        return `${(num * 100).toFixed(0)}%`;
      }
      return `${num}%`;
    },

    /**
     * 格式化金额
     */
    formatAmount(val) {
      const num = Number(String(val).replace(/,/g, ""));
      if (Number.isNaN(num)) return val || "--";
      return num.toLocaleString("zh-CN");
    },

    /**
     * 打开返利确认弹窗
     */
    openRebateConfirmModal() {
      const list = this.currentLevelData.rebateList || [];
      if (!list.length) {
        this.$message.warning("暂无返利数据可确认");
        return;
      }
      const row = list[0];
      if (row.rebateConfirm === 1) {
        this.$message.info("该年度返利额数据已经确认无误");
        return;
      }
      this.rebateConfirmDetail = {
        id: row.id,
        year: row.year,
        rebateCoefficient: row.rebateCoefficient,
        availableRebate: row.availableRebate,
        startDate: row.startDate,
        endDate: row.endDate,
        performanceAmount: row.performanceAmount,
      };
      this.rebateConfirmVisible = true;
    },

    /** 关闭返利确认弹窗 */
    closeRebateConfirmModal() {
      this.rebateConfirmVisible = false;
    },

    /**
     * 弹窗内点击「确认无误」，调用返利确认 API
     */
    submitRebateConfirm() {
      const id = this.rebateConfirmDetail.id;
      if (!id) return;
      this.confirmLoading = true;
      confirmRebate(id)
        .then(() => {
          this.confirmLoading = false;
          this.rebateConfirmVisible = false;
          this.$message.success("已成功确认返利额数据无误");
          this.fetchPerformanceData();
          this.$emit("rebate-confirmed", {
            level: this.activeLevel,
            detail: { ...this.rebateConfirmDetail },
          });
        })
        .catch(() => {
          this.confirmLoading = false;
        });
    },

    /**
     * 表格金额千分位
     */
    formatRebateAmount(amount) {
      const num = Number(amount);
      if (Number.isNaN(num)) return amount || "--";
      return num.toLocaleString("zh-CN");
    },

    /**
     * 弹窗内返利金额
     */
    formatRebateMoney(amount) {
      const num = Number(amount);
      if (Number.isNaN(num)) return "--";
      return `¥${num.toLocaleString("zh-CN")}`;
    },
  },
};
</script>

<style lang="less" scoped>
.performanceInfo {
  width: 100%;
}
.performanceInfoHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e6edf0;
  margin-bottom: 24px;
  padding-bottom: 4px;
}
.performanceInfoTabs {
  display: flex;
  gap: 48px;
}
.performanceInfoTab {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 0 0 12px;
  font-size: 16px;
  font-weight: 500;
  color: #829199;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: color 0.2s, border-color 0.2s;
  &:hover {
    color: #525f66;
  }
  &.is-active {
    color: #00d8c3;
    border-bottom-color: #00d8c3;
  }
}
.performanceInfoConfirmBtn {
  margin-bottom: 8px;
  background: #00d8c3;
  border-color: #00d8c3;
  &:hover,
  &:focus {
    background: #00c2b0;
    border-color: #00c2b0;
  }
}
.performanceInfoAlert {
  margin-bottom: 24px;
  /deep/ .el-alert__title {
    font-size: 14px;
    line-height: 1.6;
    color: #525f66;
  }
}
.performanceInfoPolicyWrap {
  margin-bottom: 24px;
  background: #f8fafc;
  border-radius: 4px;
  padding: 20px;
  border: 1px solid #f1f5f9;
  text-align: left;
}
.performanceInfoPolicyRow {
  display: flex;
  font-size: 14px;
  line-height: 1.6;
  color: #334155;
  margin-bottom: 8px;
  &:last-child {
    margin-bottom: 0;
  }
  &.mt-12 {
    margin-top: 12px;
  }
  .policyLabel {
    color: #64748b;
    font-weight: 500;
    flex-shrink: 0;
  }
  .policyValue {
    color: #1e293b;
    font-weight: 500;
    word-break: break-all;
    &.highlight {
      color: #00d8c3;
      font-weight: 600;
      font-size: 16px;
    }
  }
}
.performanceInfoTable {
  width: 100%;
  /deep/ th {
    background: #f7f8fa !important;
    color: #829199;
    font-weight: 500;
  }
}
.performanceInfoAmount {
  font-weight: 600;
  color: #00161a;
}

/* 返利额确认弹窗 */
.rebateConfirmOverlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(4px);
}
.rebateConfirmCard {
  width: 100%;
  max-width: 480px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}
.rebateConfirmHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-bottom: 1px solid #f0f0f0;
}
.rebateConfirmTitle {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
}
.rebateConfirmClose {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 4px;
  cursor: pointer;
  color: #9ca3af;
  font-size: 18px;
  line-height: 1;
  &:hover {
    color: #6b7280;
  }
}
.rebateConfirmBody {
  padding: 24px;
}
.rebateConfirmRows {
  margin-bottom: 24px;
}
.rebateConfirmRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 12px;
  margin-bottom: 12px;
  border-bottom: 1px dashed #e5e7eb;
  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: 0;
  }
}
.rebateConfirmRowLabel {
  font-size: 14px;
  color: #6b7280;
}
.rebateConfirmRowValue {
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
}
.rebateConfirmRowAmount {
  font-size: 20px;
  font-weight: 700;
  color: #22c55e;
}
.rebateConfirmWarn {
  display: flex;
  gap: 12px;
  padding: 16px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 8px;
}
.rebateConfirmWarnIcon {
  flex-shrink: 0;
  font-size: 20px;
  color: #f97316;
  margin-top: 2px;
}
.rebateConfirmWarnText {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: #9a3412;
}
.rebateConfirmFooter {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  background: rgba(249, 250, 251, 0.8);
}
.rebateConfirmCancelBtn {
  border-color: #d1d5db;
  color: #4b5563;
  &:hover,
  &:focus {
    color: #374151;
    border-color: #9ca3af;
    background: #fff;
  }
}
.rebateConfirmSubmitBtn {
  background: #00d8c3;
  border-color: #00d8c3;
  &:hover,
  &:focus {
    background: #00c2b0;
    border-color: #00c2b0;
  }
}
</style>
